/**
 * HomePage
 *
 * Broad K through 12 brand level page that lives at `/`. Speaks to the
 * universal pain (the weekend, the voice, the dread) without locking
 * the visitor into one ICP. Campaign specific landing pages like
 * `/ontario/learning-skills` go deeper for a single audience.
 *
 * Copy choices, for the record. Hero leads with the concrete pain: report
 * card comments eating a weekend. The proof is curriculum-tailored drafts
 * that start closer to what teachers actually need. Capability cards stay
 * launch-focused: regional report-card comments, rubrics, and saved/editable
 * drafts. Pricing strip is identical to the Ontario LS landing page so the
 * two pages feel like one product, not two.
 */

import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, GraduationCap, Table, ClipboardList } from "lucide-react";
import Logo from "../../components/ui/Logo";
import { BILLING_PLANS } from "../../config/billingPlans";
import { track } from "../../lib/analytics";
import SampleProof from "../../components/marketing/SampleProof";

/**
 * E40: track which landing-page signup CTA gets clicked, broken down by
 * placement (top-nav, hero, pricing-yearly, pricing-quarterly, final-cta).
 * Lets us measure the landing → /signup conversion mechanically and see
 * which placement actually drives intent, instead of inferring from
 * pageview ratios. Same Link element under the hood — only adds an
 * onClick that fires the event before navigation.
 */
function TrackedSignupLink(
  props: React.ComponentProps<typeof Link> & { placement: string },
) {
  const { placement, onClick, children, ...rest } = props;
  return (
    <Link
      {...rest}
      onClick={(e) => {
        track("landing_cta_clicked", { placement, target: String(props.to) });
        onClick?.(e);
      }}
    >
      {children}
    </Link>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-stone-50 font-sans text-stone-900 selection:bg-brand-100 selection:text-brand-900">
      {/* Skip-to-main link for keyboard users — first focusable
          element, sr-only until focused. Same pattern the dashboard
          shell uses (commit c2c2fb6). */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-3 focus:py-2 focus:bg-brand-700 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:ring-offset-2"
      >
        Skip to main content
      </a>
      <Nav />
      {/* <main> landmark — SR shortcut to skip global nav and jump
          to page content. Wraps everything between Nav and Footer. */}
      <main id="main" tabIndex={-1}>
        <Hero />
        <SampleProof />
        <PullQuote />
        <Capabilities />
        <Pricing />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

function Nav() {
  return (
    <nav aria-label="Site" className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
      <div className="h-16 px-4 sm:px-6 lg:px-10 flex items-center gap-4 max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-3 min-w-0">
          <Logo className="w-9 h-9 flex-shrink-0" aria-hidden="true" />
          <span className="text-lg font-bold text-stone-900 truncate">
            TeacherDashboard
          </span>
        </Link>

        <div className="hidden md:flex items-center justify-center gap-8 flex-1">
          <a
            href="#what"
            className="text-sm font-medium text-stone-600 hover:text-brand-700 transition-colors"
          >
            What we do
          </a>
          <Link
            to="/ontario/learning-skills"
            className="text-sm font-medium text-stone-600 hover:text-brand-700 transition-colors"
          >
            For Ontario teachers
          </Link>
          <a
            href="#pricing"
            className="text-sm font-medium text-stone-600 hover:text-brand-700 transition-colors"
          >
            Pricing
          </a>
        </div>

        <div className="flex items-center gap-3 ml-auto">
          <Link
            to="/login"
            className="hidden md:block text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
          >
            Sign in
          </Link>
          <TrackedSignupLink
            placement="top_nav"
            to="/signup"
            className="px-4 py-2 bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
          >
            Try it free
          </TrackedSignupLink>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_15%,rgba(77,56,115,0.12),transparent_55%),radial-gradient(circle_at_85%_10%,rgba(245,158,11,0.10),transparent_50%)]" />

      <div className="px-4 sm:px-6 lg:px-10 max-w-5xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/70 backdrop-blur-sm border border-stone-200 rounded-full text-stone-700 text-xs font-semibold uppercase tracking-[0.12em] mb-7 shadow-sm">
          <span aria-hidden="true">🇨🇦</span>
          Built in Canada · For K–12 teachers
        </div>

        <h1 className="font-sans text-4xl md:text-6xl font-bold tracking-tight text-stone-900 leading-[1.15] mb-8">
          Get your weekend back.
          <br />
          <span className="text-brand-700">Finish comments in hours, not weekends.</span>
        </h1>

        <p className="text-lg md:text-xl text-stone-600 max-w-2xl mx-auto mb-10 leading-relaxed">
          TeacherDashboard helps teachers finish report card comments faster,
          with drafts tailored to your curriculum that still sound human.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5">
          <TrackedSignupLink
            placement="hero"
            to="/signup"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 bg-brand-700 hover:bg-brand-800 text-white font-semibold rounded-xl text-base transition-all shadow-lg shadow-brand-600/20 hover:shadow-xl hover:-translate-y-0.5"
          >
            Try it free
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </TrackedSignupLink>
        </div>
        <p className="text-sm text-stone-500">
          Choose the student, level, and strengths. Review instead of starting from scratch.
        </p>
      </div>
    </section>
  );
}


function PullQuote() {
  return (
    <section className="px-4 sm:px-6 lg:px-10 py-20 bg-white border-y border-stone-100">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-sans text-3xl md:text-4xl font-bold text-stone-900 leading-snug">
          Report card comments tailored to your curriculum.
        </h2>
        <p className="mt-6 text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
          Choose the student, level, and strengths. TeacherDashboard drafts a
          clear, parent-ready comment using the right curriculum language, so
          you can review instead of starting from scratch.
        </p>
        <p className="mt-6 text-sm text-stone-500 max-w-xl mx-auto leading-relaxed">
          Ontario Learning Skills use E / G / S / N. BC comments use the
          Proficiency Scale. Manitoba behaviours use C / U / S / R. The
          structure is already baked in.
        </p>
      </div>
    </section>
  );
}

const CAPABILITIES = [
  {
    Icon: GraduationCap,
    title: "Comments tailored to your curriculum",
    body: "Ontario Learning Skills, Kindergarten COL, Ontario subject comments, BC Proficiency Scale comments, and Manitoba Learning Behaviours. The right scales and language are already there.",
    tag: "Region-specific",
    href: "/ontario/learning-skills",
    cta: "See an Ontario example",
    available: true,
  },
  {
    Icon: Table,
    title: "Rubrics without the blank-page spiral",
    body: "Paste the assignment, pick the grade and subject, and get a clean editable rubric you can use, revise, or export.",
    tag: "Any classroom",
    href: "/tools/rubric",
    cta: "Try without signing up",
    available: true,
  },
  {
    Icon: ClipboardList,
    title: "Review instead of starting from scratch",
    body: "Start with a clear draft, edit it in place, copy it out, or come back to saved work when report-card brain fog hits.",
    tag: "Built for the workflow",
    href: "/login",
    cta: "Try it free",
    available: true,
  },
];

function Capabilities() {
  return (
    <section
      id="what"
      className="scroll-mt-24 px-4 sm:px-6 lg:px-10 py-20 bg-stone-50"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-sans text-3xl md:text-4xl font-bold text-stone-900 mb-3">
            Built for the report-card workflow
          </h2>
          <p className="text-stone-600 text-lg">
            The student details, reporting scale, curriculum language, and draft
            structure are already set up.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {CAPABILITIES.map((cap) => (
            <CapabilityCard key={cap.title} {...cap} />
          ))}
        </div>
      </div>
    </section>
  );
}

interface CapabilityCardProps {
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  tag: string;
  href: string | null;
  cta: string | null;
  available: boolean;
}

function CapabilityCard({ Icon, title, body, tag, href, cta, available }: CapabilityCardProps) {
  const inner = (
    <>
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${
          available ? "bg-brand-50 text-brand-700" : "bg-stone-100 text-stone-400"
        }`}
      >
        <Icon className="w-5 h-5" aria-hidden="true" />
      </div>
      <h3
        className={`text-lg font-bold leading-snug mb-2 ${
          available ? "text-stone-900" : "text-stone-500"
        }`}
      >
        {title}
      </h3>
      <p
        className={`text-sm leading-relaxed flex-1 ${
          available ? "text-stone-600" : "text-stone-400"
        }`}
      >
        {body}
      </p>
      <div
        className={`mt-4 inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.12em] ${
          available ? "text-brand-800" : "text-stone-400"
        }`}
      >
        {tag}
      </div>
      {available && cta && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-stone-900">
          {cta}
          <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
        </div>
      )}
    </>
  );

  const base =
    "rounded-2xl border border-stone-200 bg-white p-6 shadow-sm flex flex-col";

  if (!available || !href) {
    return <div className={`${base} opacity-90`} aria-disabled>{inner}</div>;
  }

  return (
    <Link
      to={href}
      className={`${base} hover:border-brand-200 hover:shadow-md transition-all`}
    >
      {inner}
    </Link>
  );
}

function Pricing() {
  return (
    <section
      id="pricing"
      className="scroll-mt-24 px-4 sm:px-6 lg:px-10 py-20 bg-white"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-sans text-3xl md:text-4xl font-bold text-stone-900 mb-3">
            Less than a coffee a month
          </h2>
          <p className="text-stone-600 text-lg">
            Same Pro features either way. Pick the cadence that fits your
            budget.
          </p>
          <p className="mt-4 text-sm font-medium text-stone-700">
            3-day free trial · 30-day money-back guarantee · Cancel anytime
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
          {BILLING_PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-2xl border p-6 shadow-sm flex flex-col ${
                plan.isRecommended
                  ? "border-brand-300 bg-white ring-2 ring-brand-200"
                  : "border-stone-200 bg-white"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-stone-900">
                  {plan.name}
                </h3>
                {plan.badge && (
                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      plan.isRecommended
                        ? "bg-brand-700 text-white"
                        : "bg-stone-100 text-stone-600"
                    }`}
                  >
                    {plan.badge}
                  </span>
                )}
              </div>

              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold tracking-tight text-stone-900">
                  {plan.monthlyPriceLabel}
                </span>
                <span className="text-base font-semibold text-stone-500">
                  / month
                </span>
              </div>
              <p className="text-sm text-stone-600">{plan.billingLabel}</p>
              <p className="mt-1 text-xs text-stone-500">
                {plan.billedAmountLabel}
              </p>

              {plan.savingsLabel && (
                <div className="mt-3 inline-flex self-start rounded-md bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                  {plan.savingsLabel}
                </div>
              )}

              <ul className="mt-5 space-y-2 text-sm text-stone-700 flex-1">
                {[
                  "Unlimited Learning Skills comments",
                  "Unlimited subject comments and rubrics",
                  "Saved drafts and editable history",
                  "Export and copy when you are ready",
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-brand-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <TrackedSignupLink
                placement={`pricing_${plan.id}`}
                to="/signup"
                className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors ${
                  plan.isRecommended
                    ? "bg-brand-700 hover:bg-brand-800 text-white"
                    : "bg-stone-900 hover:bg-stone-800 text-white"
                }`}
              >
                Start free trial
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </TrackedSignupLink>
            </div>
          ))}
        </div>

        <p className="mt-6 text-center text-sm text-stone-500">
          School or board licensing? Email{" "}
          <a
            href="mailto:hello@teacherdashboard.ai"
            className="font-semibold text-stone-700 hover:text-stone-900"
          >
            hello@teacherdashboard.ai
          </a>
          .
        </p>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="px-4 sm:px-6 lg:px-10 py-20 bg-stone-50">
      <div className="max-w-3xl mx-auto rounded-3xl bg-gradient-to-br from-brand-700 to-brand-800 text-white text-center p-10 md:p-14 shadow-2xl shadow-brand-600/20">
        <h2 className="font-sans text-3xl md:text-5xl font-bold mb-4 leading-[1.1]">
          Get your report card weekend back.
        </h2>
        <p className="text-lg text-brand-100 mb-8 max-w-xl mx-auto">
          Finish comments in hours instead of weekends, with drafts that match
          your curriculum and still sound human.
        </p>
        <TrackedSignupLink
          placement="final_cta"
          to="/signup"
          className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-brand-800 font-bold rounded-xl text-base hover:bg-brand-50 transition-colors shadow-lg"
        >
          Try it free
          <ArrowRight className="w-5 h-5" aria-hidden="true" />
        </TrackedSignupLink>
        <p className="mt-4 text-sm text-brand-200">3-day free trial · No charge until day 4 · Cancel anytime</p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="px-4 sm:px-6 lg:px-10 py-10 bg-white border-t border-stone-100">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2">
          <Logo className="w-7 h-7" aria-hidden="true" />
          <span className="text-sm font-bold text-stone-900">
            TeacherDashboard
          </span>
        </Link>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-500 justify-center">
          <a href="#what" className="hover:text-stone-900">
            What we do
          </a>
          <Link to="/ontario/learning-skills" className="hover:text-stone-900">
            For Ontario
          </Link>
          <a href="#pricing" className="hover:text-stone-900">
            Pricing
          </a>
          <Link to="/login" className="hover:text-stone-900">
            Sign in
          </Link>
          <Link to="/privacy" className="hover:text-stone-900">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-stone-900">
            Terms
          </Link>
          <Link to="/contact" className="hover:text-stone-900">
            Contact
          </Link>
        </div>
        <div className="text-xs text-stone-500">
          © 2026 TeacherDashboard
        </div>
      </div>
    </footer>
  );
}
